import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";

export default function Navbar({ page, setPage }) {
  const { t } = useTranslation("common");
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const btnRef = useRef(null);

  // Close mobile menu on outside click or ESC
  useEffect(() => {
    function onClickOutside(e) {
      if (!open) return;
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        btnRef.current &&
        !btnRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    }
    function onEsc(e) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onClickOutside);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const NavButton = ({ active, onClick, children }) => (
    <button
      onClick={() => {
        onClick?.();
        setOpen(false); // close mobile menu after navigation
      }}
      className={`rounded-brand px-4 py-2 w-full sm:w-auto text-left sm:text-center transition
        ${
          active
            ? "bg-redglass-primary text-white shadow-brand ring-1 ring-redglass-ring/40"
            : "bg-white/60 border border-white/70 backdrop-blur hover:bg-white text-redglass-ink"
        }`}
    >
      {children}
    </button>
  );

  return (
    <header className="sticky top-0 z-20 backdrop-blur bg-white/80 border-b border-redglass-light/60">
      <div className="max-w-6xl mx-auto px-4 py-3 flex items-center">
        {/* Left: brand wordmark */}
        <button
          onClick={() => {
            setPage?.("home");
            if (typeof window !== "undefined") {
              const base = window.location.pathname + window.location.search;
              if (window.location.hash)
                window.history.replaceState({}, "", base);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }
          }}
          onMouseUp={(e) => e.currentTarget.blur()} // prevent sticky focus after click
          aria-label={t("brand")}
          className="group text-2xl sm:text-3xl md:text-4xl font-semibold leading-none text-redglass-ink cursor-pointer select-none"
          style={{ background: "transparent" }}
        >
          <span className="sr-only">{t("brand")}</span>

          <span className="inline-flex items-baseline gap-0 align-baseline">
            {/* Brand 'ed' stays red always */}
            <span className="-ml-1 text-redglass-primary">De</span>
            {/* 'GlassAuto' never changes to red; subtle hover only */}
            <span className="transition group-hover:opacity-90">mo</span>
          </span>
        </button>

        {/* Right group: pushed to edge; Language first, Nav last */}
        <div className="ml-auto flex items-center gap-2">
          {/* Desktop nav — now flush right */}
          <nav
            className="hidden sm:flex items-center gap-2"
            role="navigation"
            aria-label="Primary"
          >
            <NavButton active={page === "home"} onClick={() => setPage("home")}>
              {t("nav.home")}
            </NavButton>
            <NavButton
              active={page === "gallery"}
              onClick={() => {
                setPage("gallery");
                if (
                  typeof window !== "undefined" &&
                  window.location.hash !== "#gallery"
                ) {
                  window.location.hash = "#gallery";
                }
              }}
            >
              {t("nav.gallery")}
            </NavButton>
          </nav>
          <LanguageSwitcher />
          {/* Mobile hamburger (hidden on desktop) */}
          <button
            ref={btnRef}
            onClick={() => setOpen((v) => !v)}
            className="sm:hidden rounded-xl p-2 hover:bg-white text-redglass-ink"
            aria-label="Toggle menu"
            aria-expanded={open}
            aria-controls="mobile-menu"
          >
            <svg
              width="24"
              height="24"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              {open ? (
                <path
                  d="M6 18L18 6M6 6l12 12"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              ) : (
                <path
                  d="M4 6h16M4 12h16M4 18h16"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              )}
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile dropdown (ONLY nav links) */}
      <div
        id="mobile-menu"
        ref={menuRef}
        className={`sm:hidden bg-white border-t overflow-hidden transition-[max-height] duration-300 ${
          open ? "max-h-96" : "max-h-0"
        }`}
      >
        <div className="px-4 py-3 flex flex-col gap-2">
          <NavButton active={page === "home"} onClick={() => setPage("home")}>
            {t("nav.home")}
          </NavButton>
          <NavButton
            active={page === "gallery"}
            onClick={() => {
              setPage("gallery");
              if (
                typeof window !== "undefined" &&
                window.location.hash !== "#gallery"
              ) {
                window.location.hash = "#gallery";
              }
            }}
          >
            {t("nav.gallery")}
          </NavButton>
        </div>
      </div>
    </header>
  );
}
